import { AxiosResponse } from 'axios'
import React, { useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useForm } from 'react-final-form'
import styled from 'styled-components'

import Alert from '../../../../../1stlog-ui/src/components/Alert'
import Button from '../../../../../1stlog-ui/src/components/Button'
import Field, {
  Label,
  useField,
} from '../../../../../1stlog-ui/src/components/Field'
import Input from '../../../../../1stlog-ui/src/components/Input'
import LoaderOverlay from '../../../../../1stlog-ui/src/components/LoaderOverlay'
import { request } from '../../../../../1stlog-ui/src/utils'
import { TLabelActivatorResult } from '../../TReturnApp'
import Scanner from './Scanner'

type TLabelActivatorProps = {
  shortcode: string
}

type TAlert = {
  message: string
  type: string
  dismissible: boolean
}

const Wrapper = styled.div`
  position: relative;
`

async function requestReturnValidate(
  barcode: string,
  shortcode: string,
): Promise<TLabelActivatorResult> {
  const { data } = (await request({
    method: 'post',
    url: '/return/validate',
    data: {
      barcode1: barcode,
      shortcode,
    },
  })) as AxiosResponse<any>
  return data
}

const LabelActivator = (props: TLabelActivatorProps): React.ReactElement => {
  const { shortcode } = props
  // loader overlay
  // confirm
  // scanner module

  const form = useForm()

  const [isLoading, setIsLoading] = useState(false)
  const [barcode, setBarcode] = useField('barcode', { asState: true })
  const [activatedBarcode, setActivatedBarcode] = useField('activatedBarcode', {
    asState: true,
  })
  const [barcodeSearchRequired, setBarcodeSearchRequired] = useField(
    'barcodeSearchRequired',
    {
      asState: true,
    },
  )
  const [isLabelActivated, setIsLabelActivated] = useField('isLabelActivated', {
    asState: true,
  })
  const [alert, setAlert] = useState<TAlert>()

  const clearAlert = (): void => setAlert(undefined)

  const clearLabel = (): void => {
    setBarcode('')
    setIsLabelActivated(false)
    clearAlert()
  }

  // handle required barcode search
  //
  const requestLabel = async (code: string): Promise<void> => {
    try {
      setIsLoading(true)
      setAlert(undefined)
      setIsLabelActivated(false)
      const result = await requestReturnValidate(code, shortcode)

      if (!result.barcodeIsAllowed) {
        setAlert({
          message: i18n.t('tooltip_orderactivator_alreadyused_variable', {
            barcode: code,
            defaultValue:
              'Der gesuchte Barcode "{{barcode}}" wurde bereits verwendet. Bitte entfernen Sie das Label umgehend und entsorgen Sie es. Das Label darf unter keinen Umständen mehr genutzt werden. Bitte bekleben Sie das Paket mit einem neuen, gültigen Versandlabel.',
          }),
          type: 'danger',
          dismissible: true,
        })
        setBarcode('')
        return
      }
      if (result.barcodeHasSleepingOrder) {
        // label exists, set result.address and shouldSubmit=true
        setAlert({
          message: i18n.t('tooltip_orderactivator_orderfound', {
            defaultValue:
              'Barcode "{{barcode}}" wurde gefunden und wird für diesen Auftrag verwendet.',
            barcode: code,
          }),
          type: 'info',
          dismissible: false,
        })
        setActivatedBarcode(code)
        setIsLabelActivated(true)

        const { address } = result
        if (address) {
          form.batch(() => {
            form.change('addressCanBeEdited', !!result.addressCanBeEdited)
            form.change('pickup.address', address)
            form.change('colis', [])
          })
          setTimeout(() => {
            form.change('colis', [
              {
                weight: address.weight || 0,
                length: address.length || 0,
                height: address.height || 0,
                width: address.width || 0,
              },
            ])
          }, 0)
        }
        // // TODO: jump to first coli input field FIXME: FIX THIS
        // setTimeout(() => {
        //   form.change('colis[0].content', 'harry')
        //   form.change('colis.0.content', 'potter')

        // }, 1000)
        return
      }
      setAlert({
        message: i18n.t(
          'tooltip_orderactivator_noorder_update',
          'Zu diesem Barcode liegt noch kein Auftrag vor. Bitte prüfen Sie, ob der Barcode korrekt eingetragen wurde. Wenn ja geben Sie bitte Ihre Versandadresse an.',
        ),
        type: 'warning',
        dismissible: true,
      })
    } catch (err) {
      //
      setAlert({
        message: i18n.t(
          'retour_label_unknown_error',
          'Fehler beim Abfragen des Labels. Bitte versuchen Sie es später erneut.',
        ),
        type: 'danger',
        dismissible: true,
      })
    } finally {
      setIsLoading(false)
    }
  }
  const onSearch = (): void => {
    requestLabel(barcode)
  }

  const onScan = (code: string): void => {
    setBarcode(code)
    requestLabel(code)
  }

  const clearBarcode = (): void => setBarcode('')

  return (
    <Wrapper>
      <LoaderOverlay isLoading={isLoading} offset={-10} />
      {alert && (
        <Row>
          <Col>
            <Alert
              variant={alert.type}
              onClose={alert.dismissible ? clearAlert : undefined}
            >
              {alert.message}
              {/* {isLabelActivated && (
                <div className="mt-2">
                  <strong>Label doch nicht verwenden</strong>
                </div>
              )} */}
            </Alert>
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <div className="d-flex">
            <div className="flex-fill">
              <Field
                name="barcode"
                label={i18n.t('activate_label', 'Label aktivieren')}
              >
                <Input onClear={clearBarcode} />
              </Field>
            </div>
            <div className="ml-2">
              <Label hideLabel>QR-Code</Label>
              <div>
                <Scanner onScanned={onScan} />
              </div>
            </div>
            <div className="ml-2">
              <Label hideLabel>Suchen</Label>
              <div>
                <Button primary onClick={onSearch}>
                  {i18n.t('search', 'Suchen')}
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Wrapper>
  )
}

export default LabelActivator
